import { useState, useRef } from 'react';
import type { ComputedColumn } from './types';
import { useTheme } from '@simplywallst/ui-core';
import { VisxText } from '@/components/VisxText';
import type { DefaultTheme } from 'styled-components';

const LONG_HOVER_DELAY = 250;
interface Props {
  data: ComputedColumn[];
  height: number;
  showColumnKey?: boolean;
  selectingColumnKey?: string;
  onSelectColumn?: (key: string) => void;
}

type GetUIVariantParams = {
  theme: DefaultTheme;
  isSelecting: boolean;
  isHovering: boolean;
};

const getUiVariant = ({
  theme,
  isSelecting,
  isHovering,
}: GetUIVariantParams) => {
  const uiVariants = {
    selected: {
      fill: theme.color.black,
      fillOpacity: 0.5,
      formattedValueFill: theme.color.textSolid,
    },
    hovering: {
      fill: theme.color.brand01,
      fillOpacity: 0.1,
      formattedValueFill: theme.color.textSoft,
    },
    default: {
      fill: 'transparent',
      fillOpacity: 0,
      formattedValueFill: theme.color.textSoft,
    },
  };

  if (isSelecting) {
    return uiVariants.selected;
  }
  if (isHovering) {
    return uiVariants.hovering;
  }
  return uiVariants.default;
};

export const StackBars = ({
  data,
  showColumnKey,
  selectingColumnKey,
  onSelectColumn,
  height,
}: Props) => {
  const theme = useTheme();
  const offsetTop = theme.x5;
  const offsetBottom = theme.x5;
  const columnPadding = theme.x1;

  const [selectingKeyState, setSelectingkeyState] = useState<string>(
    data?.[0]?.key || ''
  );
  const [hoveringKey, setHoveringKey] = useState<string | null>(null);

  const selectTimeoutId = useRef<NodeJS.Timeout>();

  const selectingKey = selectingColumnKey || selectingKeyState;

  const selectColumn = (key: string) => {
    if (!selectingColumnKey) {
      setSelectingkeyState(key);
    }
    onSelectColumn?.(key);
  };

  const handleMouseEnter = (key: string) => {
    setHoveringKey(key);
    const timeout = setTimeout(() => {
      selectColumn(key);
    }, LONG_HOVER_DELAY);

    selectTimeoutId.current = timeout;
  };

  const handleMouseLeave = () => {
    setHoveringKey(null);
    clearTimeout(selectTimeoutId.current);
  };

  const handleClick = (key: string) => {
    selectColumn(key);
  };

  return data.map((column) => {
    const isSelecting = selectingKey === column.key;
    const isHovering = hoveringKey === column.key;
    const uiVariant = getUiVariant({
      theme,
      isSelecting,
      isHovering,
    });

    return (
      <g key={column.key}>
        <rect
          style={{ cursor: 'pointer' }}
          x={column.x}
          y="0"
          width={column.width}
          height={height}
          fill={uiVariant.fill}
          fillOpacity={uiVariant.fillOpacity}
          onClick={() => handleClick(column.key)}
          onMouseEnter={() => handleMouseEnter(column.key)}
          onMouseLeave={() => handleMouseLeave()}
        />
        {isSelecting && (
          <line
            x1={column.x}
            y1="0"
            x2={column.x + column.width}
            y2="0"
            stroke={theme.color.brand01}
            strokeWidth="1"
          />
        )}
        {column.bars.map(
          ({ key, color, height: barHeight, width: barWidth, x, y }) => {
            return (
              <rect
                style={{ cursor: 'pointer' }}
                key={`bar-${column.key}-${key}`}
                x={x}
                y={y}
                width={barWidth}
                height={barHeight}
                fill={color}
                onClick={() => handleClick(column.key)}
                onMouseEnter={() => handleMouseEnter(column.key)}
                onMouseLeave={() => handleMouseLeave()}
              />
            );
          }
        )}
        {column.formattedValue && (
          <VisxText
            typography="xSmallStrong"
            y={column.top}
            x={column.x + columnPadding}
            dx={0}
            dy={-offsetTop / 2}
            lineHeight="1.5em"
            width={column.width}
            verticalAnchor="middle"
            fill={uiVariant.formattedValueFill}
          >
            {column.formattedValue}
          </VisxText>
        )}
        {showColumnKey && (
          <VisxText
            typography="xSmall"
            y={height + offsetBottom}
            x={column.x + columnPadding}
            dy={-offsetBottom / 2}
            lineHeight="1.5em"
            width={column.width - columnPadding * 2}
            fill={theme.color.textSolid}
            verticalAnchor="middle"
            textAnchor="start"
            dx={0}
          >
            {column.key}
          </VisxText>
        )}
      </g>
    );
  });
};
